import { Box, Container, Typography, Button, Switch, FormControlLabel, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TimeNavigationControls from '../components/TimeNavigationControls';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { useAirportDataAccess } from '../hooks/useAirportDataAccess';
import { useOperationalReplayData } from '../hooks/timeline/useOperationalReplayData';
import { ALL_AIRPORTS_DATA, ALL_UK_LOCATIONS, SIDELINED_UK_LOCATIONS, ALL_EU_LOCATIONS } from '../data/locations';
import OperationalReplayTimeline from '../components/timeline/OperationalReplayTimeline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import { VisibilityLegend } from '../components/common/VisibilityLegend';
import StatusAlert from '../components/common/StatusAlert';

export default function OperationalReplayPage() {
  const { idOrIcao } = useParams<{ idOrIcao: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { locations: accessibleLocations, loading: locationsLoading } = useAirportDataAccess();

  const location = useMemo(() => {
    if (!idOrIcao) return null;
    const numericId = parseInt(idOrIcao, 10);
    if (!isNaN(numericId)) {
      return ALL_AIRPORTS_DATA.find(airport => airport.id === numericId);
    }
    const icaoCode = idOrIcao.toUpperCase();
    return ALL_AIRPORTS_DATA.find(airport => airport.code === icaoCode);
  }, [idOrIcao]);

  const hasAccess = useMemo(() => {
    if (!location) return false;
    return accessibleLocations.includes(location.id);
  }, [location, accessibleLocations]);

  const isMetarLocation = useMemo(() => {
    if (!location) return false;
    return ALL_UK_LOCATIONS.includes(location.id)
      || SIDELINED_UK_LOCATIONS.includes(location.id)
      || ALL_EU_LOCATIONS.includes(location.id);
  }, [location]);

  const [customNow, setCustomNow] = useState<Date | null>(() => {
    const timeParam = new URLSearchParams(window.location.search).get('time');
    if (timeParam) {
      const date = new Date(timeParam);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  });

  const [showControls, setShowControls] = useState(() => {
    const showControlsParam = new URLSearchParams(window.location.search).get('showControls');
    return showControlsParam === 'true' || !!customNow;
  });

  const [useUTCTime, setUseUTCTime] = useState(true);

  const replayData = useOperationalReplayData(
    location?.id || null,
    isMetarLocation,
    customNow
  );

  useEffect(() => {
    if (location) {
      const env = import.meta.env.VITE_APP_ENV;
      let title = `Verification Replay - ${location.name} Airport (${location.code}) - FogCast`;
      if (env && env !== 'production') {
        title += ` - ${env}`;
      }
      document.title = title;
    }
    return () => {
      const env = import.meta.env.VITE_APP_ENV;
      let title = 'FogCast';
      if (env && env !== 'production') {
        title += ` - ${env}`;
      }
      document.title = title;
    };
  }, [location]);

  const updateUrlWithTime = (newTime: Date | null, keepControls?: boolean) => {
    const url = new URL(window.location.href);
    if (newTime) {
      url.searchParams.set('time', newTime.toISOString());
      setCustomNow(newTime);
      if (keepControls || showControls) {
        url.searchParams.set('showControls', 'true');
        setShowControls(true);
      } else {
        url.searchParams.delete('showControls');
        setShowControls(false);
      }
    } else {
      url.searchParams.delete('time');
      url.searchParams.delete('showControls');
      setCustomNow(null);
      setShowControls(false);
    }
    window.history.pushState({}, '', url.toString());
  };

  useEffect(() => {
    const handlePopState = () => {
      const timeParam = new URLSearchParams(window.location.search).get('time');
      const showControlsParam = new URLSearchParams(window.location.search).get('showControls');
      if (timeParam) {
        const date = new Date(timeParam);
        setCustomNow(isNaN(date.getTime()) ? null : date);
      } else {
        setCustomNow(null);
      }
      setShowControls(showControlsParam === 'true');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const formatLastUpdated = (date: Date): string => {
    const timeString = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${timeString} (${timezone})`;
  };

  if (locationsLoading) {
    return <LoadingScreen message="Loading..." />;
  }

  if (!location || !hasAccess) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography variant="h2" gutterBottom>
            Location Not Found
          </Typography>
          <Typography variant="h4" sx={{ mb: 3, color: 'text.secondary', fontWeight: 400 }}>
            The requested location does not exist or has not been enabled for your user account.
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/${customNow ? `?now=${customNow.toISOString()}` : ''}`)}
          >
            Back to Timelines
          </Button>
        </Box>
      </Container>
    );
  }

  if (replayData.loading && replayData.panels.length === 0) {
    return <LoadingScreen message="Loading operational replay data..." />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl">
        <Box sx={{ my: { xs: 4, sm: 0 }, mb: { sm: 4 }, pt: { sm: 3 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/location/${idOrIcao}${customNow ? `?time=${customNow.toISOString()}` : ''}`)}
            sx={{ mb: 2 }}
          >
            Back to Location View
          </Button>

          {showControls && (
            <Box sx={{ mb: 2 }}>
              <TimeNavigationControls
                currentTime={customNow}
                onTimeChange={updateUrlWithTime}
                showControls={showControls}
                useUTCTime={useUTCTime}
              />
            </Box>
          )}

          <Box sx={{
            position: 'relative',
            top: 'auto',
            zIndex: 'auto',
            backgroundColor: 'transparent',
            mx: 0,
            px: 0,
            pt: 0,
            pb: 0,
            '@media (min-width: 600px) and (min-height: 500px)': {
              position: 'sticky',
              top: '-24px',
              zIndex: 3,
              backgroundColor: 'background.default',
              mx: -3,
              px: 3,
              pt: 3,
              pb: 0.5
            }
          }}>
            <Box sx={{
              mb: { xs: 1.5, md: 2 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              pb: { xs: 1.5, sm: 2 },
              gap: { xs: 1, sm: 0 },
              pt: { xs: 0, sm: 1 }
            }}>
              <Box>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
                >
                  Verification Replay
                  {customNow && (
                    <Typography
                      component="span"
                      variant="subtitle1"
                      sx={{
                        ml: { xs: 1, sm: 2 },
                        color: 'warning.main',
                        fontSize: { xs: '0.8rem', sm: '1rem' },
                        display: { xs: 'block', sm: 'inline' },
                        mt: { xs: 0.5, sm: 0 }
                      }}
                    >
                      (Using custom date)
                    </Typography>
                  )}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'text.secondary', mt: 0.5 }}
                >
                  {location.name} Airport ({location.code})
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    mt: 1,
                    px: 1.5,
                    py: 0.75,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 1,
                    display: 'inline-block',
                    borderLeft: '3px solid #ba68c8'
                  }}
                >
                  24-hour replay showing six 4-hour forecast windows, with actuals up to{' '}
                  <Typography component="span" sx={{ fontWeight: 'bold', color: '#ba68c8' }}>
                    {(() => {
                      const ref = customNow || new Date();
                      return useUTCTime
                        ? ref.toISOString().slice(0, 16).replace('T', ' ') + ' UTC'
                        : ref.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
                    })()}
                  </Typography>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!customNow && (
                  <Tooltip title="Adjust Date" placement="left">
                    <IconButton
                      onClick={() => {
                        const url = new URL(window.location.href);
                        if (showControls) {
                          url.searchParams.delete('showControls');
                          window.history.pushState({}, '', url.toString());
                          setShowControls(false);
                        } else {
                          url.searchParams.set('showControls', 'true');
                          window.history.pushState({}, '', url.toString());
                          setShowControls(true);
                        }
                      }}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                      }}
                    >
                      <EditCalendarIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={useUTCTime}
                      onChange={() => setUseUTCTime(!useUTCTime)}
                      color="primary"
                      size={isMobile ? "small" : "medium"}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ ml: 0.5, fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                      {useUTCTime ? "UTC Time" : "Local Time"}
                    </Typography>
                  }
                  sx={{
                    my: { xs: 0.5, sm: 0 },
                    mr: { xs: 0, sm: 0 },
                    '.MuiFormControlLabel-label': { fontSize: { xs: '0.75rem', sm: '1rem' } }
                  }}
                />
              </Box>
            </Box>

            <VisibilityLegend orientation="horizontal" />
          </Box>

          {replayData.panels.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <OperationalReplayTimeline
                data={replayData}
                useUTCTime={useUTCTime}
              />
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              backgroundColor: 'rgba(50, 50, 50, 0.5)',
              borderRadius: 1,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1.5, sm: 1 },
              mb: 2,
              mt: 1,
              gap: { xs: 1, sm: 0 }
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '1rem' } }}
            >
              {customNow ? (
                <>
                  Custom time: {customNow.toISOString()}
                  <span style={{
                    color: theme.palette.warning.main,
                    display: 'block',
                    marginTop: '4px',
                    fontSize: 'inherit'
                  }}>
                    (auto-refresh disabled)
                  </span>
                </>
              ) : (
                <>Last updated: {formatLastUpdated(replayData.lastUpdated)}</>
              )}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {!customNow && (
                <Box
                  component="button"
                  onClick={replayData.refresh}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    border: 'none',
                    borderRadius: 1,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 0.5 },
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    alignSelf: { xs: 'flex-start', sm: 'auto' },
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    fontWeight: 500
                  }}
                >
                  Refresh Now
                </Box>
              )}
            </Box>
          </Box>

          {replayData.warning && !replayData.error && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <StatusAlert
                dataQualityWarning={{
                  type: 'warning',
                  title: 'Incomplete Data',
                  message: replayData.warning,
                }}
              />
            </Box>
          )}

          {replayData.error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, color: 'error.main' }}>
              {replayData.error}
            </Box>
          )}
        </Box>
      </Container>
    </LocalizationProvider>
  );
}
